import { sanitizePii } from '@tankef/env';

import type { AnalyticsEvent } from '../index';
import type { AnalyticsAdapter } from './index';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

export interface GtmAdapterOptions {
  containerId: string;
  /** Imprimir cada push al dataLayer en consola */
  debug?: boolean;
}

/**
 * Adapter de Google Tag Manager.
 *
 * - init()     → inyecta el script GTM en <head> y el <noscript> en <body>
 * - track()    → window.dataLayer.push({ event, ...properties })
 * - identify() → dataLayer.push con userId para que GA4 lo reciba
 * - setUser()  → limpia / actualiza user_id en el dataLayer
 *
 * Uso:
 *   new GtmAdapter({ containerId: 'GTM-XXXXXXX' })
 */
export class GtmAdapter implements AnalyticsAdapter {
  private containerId: string;
  private debug: boolean;

  constructor({ containerId, debug = false }: GtmAdapterOptions) {
    this.containerId = containerId;
    this.debug = debug;
  }

  init(): void {
    if (!this.containerId) return;
    if (document.getElementById('gtm-script')) return; // ya inyectado

    // Inicializar dataLayer antes del script
    window.dataLayer = window.dataLayer || [];

    // <script> en <head>
    const script = document.createElement('script');
    script.id = 'gtm-script';
    script.innerHTML = `
(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${this.containerId}');
    `.trim();
    document.head.appendChild(script);

    // <noscript> en <body> (requerido por GTM para entornos sin JS)
    const noscript = document.createElement('noscript');
    noscript.id = 'gtm-noscript';
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${this.containerId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);

    if (this.debug) {
      console.info(`[GTM] Initialized with container ${this.containerId}`);
    }
  }

  track(event: AnalyticsEvent): void {
    if (!window.dataLayer) return;

    // H1: sanitizar properties para que ninguna PII llegue a GA4. Aunque la
    // política es que los call-sites NO incluyan PII en eventos, queremos
    // defensa en profundidad — un track() accidental con CURP/email/RFC se
    // enmascara antes de salir del browser. LFPDPPP minimización + GA4 ToS.
    const sanitizedProperties = event.properties
      ? (sanitizePii(event.properties, { shapeOnly: false }) as Record<string, unknown>)
      : undefined;

    const payload: Record<string, unknown> = {
      event: event.name,
      ...sanitizedProperties,
    };

    window.dataLayer.push(payload);

    if (this.debug) {
      console.info('[GTM] track', payload);
    }
  }

  identify(userId: string, traits?: Record<string, unknown>): void {
    if (!window.dataLayer) return;

    // userId es opaco (UUID server-side), no PII directa. traits sí
    // puede traer email/phone — sanitizamos para no mandarlos a GA4.
    const sanitizedTraits = traits
      ? (sanitizePii(traits, { shapeOnly: false }) as Record<string, unknown>)
      : undefined;

    window.dataLayer.push({
      event: 'identify',
      user_id: userId,
      ...sanitizedTraits,
    });

    if (this.debug) {
      console.info('[GTM] identify', userId, sanitizedTraits);
    }
  }

  setUser(user: { id: string; email?: string } | null): void {
    if (!window.dataLayer) return;

    // GA4 lee user_id del dataLayer para cross-device tracking. NO emitimos
    // email aunque venga en el argumento — el id opaco es suficiente.
    window.dataLayer.push({
      user_id: user?.id ?? null,
    });
  }
}
